// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// mui
import { Alert, Box, Card, Container, Stack, Typography } from '@mui/material';

// types
import type {
  FactValue,
  FactVersion,
  ReviewCategory,
  ReviewFact,
} from '@karehero/models';

// components
import { Button } from '@/components/atoms/Button/Button';
import { Loading } from '@/components/pages/Loading/Loading';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { FactEditSheet, type FactEdit } from './FactEditSheet';
import { FactRow } from './FactRow';

export interface FactReviewPageProps {
  isLoading: boolean;
  categories: ReviewCategory[];
  title?: string;
  description?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  /** Omit to hide the back button. */
  onBack?: () => void;
  /** Omit to render the facts read-only. */
  onSaveFact?: (factId: string, value: FactValue) => void | Promise<void>;
}

const CONFLICT_NOTICE_MS = 4500;

const conflictNotice = (count: number): string =>
  count === 1
    ? 'One answer has two versions. Pick the right one.'
    : `${count} answers have two versions. Pick the right ones.`;

/** FactReviewPage lists extracted facts by group to confirm or correct. */
export const FactReviewPage = ({
  isLoading,
  categories,
  title = 'We filled in what we could',
  description = 'Change anything we got wrong.',
  confirmLabel = 'Looks good to me',
  onConfirm,
  onBack,
  onSaveFact,
}: FactReviewPageProps) => {
  const [factDraft, setFactDraft] = useState<ReviewFact | null>(null);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [blockedCount, setBlockedCount] = useState(0);

  const facts = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        facts: category.facts.map((fact) =>
          picks[fact.id] !== undefined
            ? { ...fact, status: 'confirmed' as const, value: picks[fact.id] }
            : fact,
        ),
      })),
    [categories, picks],
  );
  const unresolved = useMemo(
    () => facts.flatMap((c) => c.facts).filter((f) => f.status === 'conflict'),
    [facts],
  );

  useEffect(() => {
    if (!blockedCount) return;
    const timer = setTimeout(() => setBlockedCount(0), CONFLICT_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [blockedCount]);

  const handleConfirm = useCallback(() => {
    if (unresolved.length === 0) return onConfirm();
    setBlockedCount(unresolved.length);
    document
      .getElementById(`fact-${unresolved[0].id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [onConfirm, unresolved]);

  const handlePickVersion = useCallback(
    (fact: ReviewFact, version: FactVersion) => {
      setPicks((prev) => ({ ...prev, [fact.id]: version.value }));
      onSaveFact?.(fact.id, version.selected ?? version.value);
    },
    [onSaveFact],
  );

  const handleEditFact = useCallback(
    (fact: ReviewFact) => setFactDraft(fact),
    [],
  );

  const handleCloseSheet = useCallback(() => setFactDraft(null), []);

  const handleSaveFact = useCallback(
    (edits: FactEdit[]) => {
      edits.forEach((edit) => onSaveFact?.(edit.id, edit.value));
      setFactDraft(null);
    },
    [onSaveFact],
  );

  if (isLoading) return <Loading />;

  return (
    <ModalPageTemplate
      onBack={onBack}
      footerSlot={
        <Box
          sx={{
            bgcolor: 'background.default',
            borderTop: 1,
            borderColor: 'custom.neutral.200',
            position: 'relative',
          }}
        >
          {blockedCount > 0 && (
            <Alert
              severity='error'
              variant='filled'
              sx={{
                position: 'absolute',
                bottom: '100%',
                left: 16,
                right: 16,
                mb: 1,
              }}
            >
              {conflictNotice(unresolved.length)}
            </Alert>
          )}
          <Container maxWidth='xs'>
            <Box sx={{ py: 2 }}>
              <Button
                variant='primary'
                size='lg'
                isFullWidth
                ariaLabel={confirmLabel}
                onPress={handleConfirm}
              >
                {confirmLabel}
              </Button>
            </Box>
          </Container>
        </Box>
      }
    >
      <PageTemplate
        maxWidth='xs'
        headerSlot={
          <PageHeader title={title} description={description} variant='h2' />
        }
        contentSlot={
          <Stack spacing={3}>
            {facts.map((category) => (
              <Stack key={category.label} spacing={1}>
                {category.label && (
                  <Typography
                    variant='overline'
                    sx={{ color: 'text.secondary' }}
                  >
                    {category.label}
                  </Typography>
                )}
                <Card sx={{ overflow: 'hidden' }}>
                  {category.facts.map((fact, index) => (
                    <FactRow
                      key={fact.id}
                      fact={fact}
                      isFirst={index === 0}
                      onEdit={
                        onSaveFact &&
                        !fact.isReadOnly &&
                        fact.status !== 'conflict'
                          ? handleEditFact
                          : undefined
                      }
                      onPickVersion={handlePickVersion}
                    />
                  ))}
                </Card>
              </Stack>
            ))}
          </Stack>
        }
      />

      {factDraft && (
        <FactEditSheet
          key={factDraft.id}
          fact={factDraft}
          onSave={handleSaveFact}
          onClose={handleCloseSheet}
        />
      )}
    </ModalPageTemplate>
  );
};

FactReviewPage.displayName = 'FactReviewPage';
export default FactReviewPage;
